export class GameAudio {
  private context: AudioContext | null = null;
  private enabled: boolean;

  constructor(enabled: boolean) {
    this.enabled = enabled;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  async unlock(): Promise<void> {
    if (!this.enabled) return;
    this.context ??= new AudioContext();
    if (this.context.state === "suspended") await this.context.resume();
  }

  shoot(): void {
    this.tone(520 + Math.random() * 120, 0.05, "square", 0.018, -260);
  }

  snipe(): void {
    this.tone(880, 0.09, "sawtooth", 0.03, -640);
    this.tone(190, 0.12, "sine", 0.04, -110, 0.02);
  }

  mortarFire(): void {
    this.tone(140, 0.16, "triangle", 0.05, -70);
  }

  explosion(strength: number): void {
    const power = Math.min(1, Math.max(0.3, strength));
    this.tone(90, 0.3, "sine", 0.08 * power, -50);
    this.tone(60, 0.4, "triangle", 0.06 * power, -25, 0.04);
    this.tone(700, 0.06, "square", 0.014 * power, -500, 0.01);
  }

  kill(streak: number): void {
    const base = 430 + Math.min(streak, 16) * 26;
    this.tone(base, 0.09, "sine", 0.04, 190);
    this.tone(base * 1.5, 0.12, "sine", 0.03, 140, 0.06);
  }

  place(): void {
    this.tone(300, 0.07, "triangle", 0.04, 90);
    this.tone(450, 0.06, "triangle", 0.03, 60, 0.05);
  }

  upgrade(): void {
    const notes = [330, 440, 660, 880];
    notes.forEach((note, index) => {
      this.tone(note, 0.1, "triangle", 0.045, 60, index * 0.06);
    });
  }

  zap(): void {
    this.tone(1200, 0.05, "square", 0.02, -700);
    this.tone(300, 0.08, "sawtooth", 0.025, -150, 0.02);
  }

  beam(): void {
    this.tone(180, 0.25, "sawtooth", 0.05, -120);
    this.tone(60, 0.35, "sine", 0.07, -30, 0.05);
  }

  denied(): void {
    this.tone(220, 0.1, "square", 0.035, -60);
    this.tone(160, 0.14, "square", 0.03, -40, 0.08);
  }

  crossing(): void {
    this.tone(150, 0.25, "sawtooth", 0.05, -60);
    this.tone(104, 0.32, "square", 0.032, -45, 0.06);
    this.tone(78, 0.4, "triangle", 0.04, -28, 0.12);
  }

  bossWarning(): void {
    this.tone(196, 0.22, "sawtooth", 0.05, 40);
    this.tone(196, 0.22, "sawtooth", 0.05, 40, 0.3);
    this.tone(147, 0.4, "sawtooth", 0.05, 30, 0.6);
  }

  waveClear(): void {
    const notes = [392, 494, 587, 784];
    notes.forEach((note, index) => {
      this.tone(note, 0.14, "triangle", 0.04, 20, index * 0.09);
    });
  }

  gameOver(): void {
    const notes = [392, 330, 262, 196];
    notes.forEach((note, index) => {
      this.tone(note, 0.24, "triangle", 0.045, -18, index * 0.19);
    });
  }

  private tone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    frequencyDelta: number,
    delay = 0,
  ): void {
    if (!this.enabled || !this.context) return;

    const now = this.context.currentTime + delay;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(40, frequency + frequencyDelta), now + duration);
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration);
  }
}
